/*
 * <ugc-videos>
 * UGC video thumbnails that open a native `<dialog class="modal">` video player. Both the
 * thumbnails and the player are <carousel-slider> scroll-snap tracks (see carousel-slider.js);
 * closing on Escape / backdrop click, focus return and the scroll lock come from the dialog and
 * modal-dialog.js. This element only:
 *   - opens the dialog on the clicked video (`[data-ugc-open="index"]`)
 *   - plays the video in view and pauses the rest as the player is swiped / navigated
 *   - mounts the `<template>` videos lazily around the current one and releases them on close
 *   - toggles play/pause (`[data-ugc-toggle]`) and the arrow keys navigate
 *
 * Slide state for styling: `data-ready` once the video can play, `data-playing` while playing,
 * `--ugc-progress` (0-1) with the playback position.
 */
if (!customElements.get("ugc-videos")) {
  /**
   * Videos on each side of the current one kept mounted with preload="auto", so navigating is
   * instant. 1 keeps at most 3 live <video> elements, well below iOS Safari's concurrent media
   * element limit.
   */
  const MOUNT_WINDOW = 1;

  class UgcVideos extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        openButton: "[data-ugc-open]",
        dialog: "dialog",
        carousel: "[data-ugc-carousel]",
        track: "[data-carousel-track]",
        slide: "[data-ugc-slide]",
        toggle: "[data-ugc-toggle]",
      };
    }

    connectedCallback() {
      this.dialog = this.querySelector(this.selectors.dialog);
      this.carousel = this.dialog?.querySelector(this.selectors.carousel);
      if (!this.carousel) return;

      this.slides = [...this.carousel.querySelectorAll(this.selectors.slide)];
      /** @type {Map<number, { video: HTMLVideoElement, controller: AbortController, setPlaying: Function }>} */
      this.mounted = new Map();
      this.currentIndex = -1;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.addEventListener("click", this.onClick.bind(this), { signal });
      this.carousel.addEventListener("carousel:change", this.onChange.bind(this), { signal });
      this.dialog.addEventListener("keydown", this.onKeydown.bind(this), { signal });
      this.dialog.addEventListener("close", this.onClose.bind(this), { signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.unmountAll();
    }

    open(index) {
      if (!this.slides[index]) return;

      if (!this.dialog.open) this.dialog.showModal();
      this.carousel.goToSlide(index, { instant: true });
      this.activate(index);
    }

    /** Plays the video at `index`, pausing the others and (un)mounting around it. */
    activate(index) {
      if (index === this.currentIndex || !this.slides[index]) return;
      this.currentIndex = index;

      // Unmount outside the window first, freeing decoders before new ones are created (iOS)
      [...this.mounted.keys()].forEach((slideIndex) => {
        if (Math.abs(slideIndex - index) > MOUNT_WINDOW) this.unmount(slideIndex);
      });

      const last = Math.min(this.slides.length - 1, index + MOUNT_WINDOW);
      for (let i = Math.max(0, index - MOUNT_WINDOW); i <= last; i++) this.mount(i);

      this.mounted.forEach(({ video }, slideIndex) => {
        if (slideIndex !== index) video.pause();
      });
      this.play(index);
    }

    play(index) {
      // Rejected when the browser blocks unmuted autoplay; the play button stays visible instead
      this.mounted.get(index)?.video.play()?.catch(() => {});
    }

    toggle(index) {
      const video = this.mounted.get(index)?.video;
      if (!video) return;

      if (video.paused) this.play(index);
      else video.pause();
    }

    mount(index) {
      if (this.mounted.has(index)) return;

      const slide = this.slides[index];
      const template = slide.querySelector("template");
      const video = template?.content.firstElementChild?.cloneNode(true);
      if (!(video instanceof HTMLVideoElement)) return;

      const toggle = slide.querySelector(this.selectors.toggle);
      const controller = new AbortController();
      const { signal } = controller;
      const setPlaying = (playing) => {
        slide.toggleAttribute("data-playing", playing);
        if (toggle)
          toggle.setAttribute(
            "aria-label",
            playing ? toggle.dataset.pauseLabel : toggle.dataset.playLabel,
          );
      };

      video.addEventListener("loadeddata", () => slide.setAttribute("data-ready", ""), { signal });
      video.addEventListener("play", () => setPlaying(true), { signal });
      video.addEventListener("pause", () => setPlaying(false), { signal });
      video.addEventListener(
        "timeupdate",
        () => {
          if (video.duration)
            slide.style.setProperty("--ugc-progress", video.currentTime / video.duration);
        },
        { signal },
      );

      video.preload = "auto";
      template.after(video);
      // Starts loading right away; Safari can otherwise defer it despite preload="auto"
      video.load();

      this.mounted.set(index, { video, controller, setPlaying });
    }

    /** Removes a mounted video, fully releasing its media pipeline. */
    unmount(index) {
      const mounted = this.mounted.get(index);
      if (!mounted) return;

      const { video, controller, setPlaying } = mounted;
      const slide = this.slides[index];
      controller.abort();
      setPlaying(false);

      video.pause();
      video.querySelectorAll("source").forEach((source) => source.remove());
      video.removeAttribute("src");
      video.load();
      video.remove();

      slide.removeAttribute("data-ready");
      slide.style.removeProperty("--ugc-progress");
      this.mounted.delete(index);
    }

    unmountAll() {
      [...(this.mounted?.keys() ?? [])].forEach((index) => this.unmount(index));
    }

    onClick(event) {
      const openButton = event.target.closest(this.selectors.openButton);
      if (openButton) return this.open(Number(openButton.dataset.ugcOpen));

      if (event.target.closest(this.selectors.toggle)) this.toggle(this.currentIndex);
    }

    onChange(event) {
      // One slide per page, so the page is the slide index
      if (this.dialog.open) this.activate(event.detail.page);
    }

    onKeydown(event) {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      // A focused track already scrolls natively
      if (event.target.closest(this.selectors.track)) return;

      event.preventDefault();
      this.carousel.goToSlide(this.currentIndex + (event.key === "ArrowLeft" ? -1 : 1));
    }

    onClose() {
      this.unmountAll();
      this.currentIndex = -1;
    }
  }

  customElements.define("ugc-videos", UgcVideos);
}
